import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ListGrid from '../../../components/ListGrid';
import EmptyScreen from '../../../components/EmptyScreen';
import { Figure, Figures } from '../../../components/Figures';
import { dayBrief, dayShort, dayShortAndClock } from '../../../lib/when';
import { listRuns, getRun, locationsIn } from './movementRuns';
import { buildV2Csv, downloadCsv } from './movementUtils';
import { downloadCsv as downloadRows } from '../../reports/reportData';
import PageLoading from '../../../components/PageLoading';

/*
  Movement, Run History. The ninth screen of Group G.

  **Added on the owner's word, 5 September 2026**: *"draw the ninth screen, the
  movement run history."* It is the only screen in OnHand drawn for something
  that did not exist in any form, no screen and no table, and it is built here
  the same day the table under it was.

  **Why it has to exist.** He asked for the photographs of handwritten movement
  pages to be deleted once the review is done, and for a record of what moved to
  be kept instead. That record had nowhere to be looked at.

  The approved drawing is `docs/design/group-g/history-list-*.png`,
  `history-run-*.png` and `history-*.png`, signed off 5 September 2026. **Three
  things in it are instructions rather than decoration:**

  - **Every run says what happened to its photographs**, still held or deleted
    with the date, so the owner's rule has a face rather than being invisible
    plumbing. Rule 9: a run whose pictures are gone and a run still holding them
    must not read the same way.
  - **An opened run is read only.** The pages it was read from no longer exist,
    so offering a correction that cannot be made would be a lie.
  - **A line that never went into the file is kept and shown.** A history that
    dropped those would report a run as cleaner than it was.

  **Called Run History and not History**, because History is already the name of
  the screen showing every count a person has submitted.
*/

/*
  What happened to a run's photographs, in the two states rule 9 asks for. The
  amber one is not a warning: a run still holding its pictures is one whose
  review is not finished, which is ordinary.
*/
function Photos({ deletedAt }) {
  if (!deletedAt) {
    return <span className="mv-photos held">Photos still held</span>;
  }
  return <span className="mv-photos gone">Photos deleted {dayBrief(deletedAt)}</span>;
}

const OUTCOME = {
  in_file:      { label: 'In the file',      cls: 'ok' },
  in_file_as_1: { label: 'In the file as 1', cls: 'warn' },
  left_out:     { label: 'Left out',         cls: 'bad' },
};

function Outcome({ value }) {
  const o = OUTCOME[value] || OUTCOME.in_file;
  return <span className={'mv-outcome ' + o.cls}>{o.label}</span>;
}

export default function HistoryTab({ onGoToPages, setBar, toast }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [open, setOpen] = useState(null);      // { run, lines }
  const [search, setSearch] = useState('');
  /*
    **Two filters on the approved drawing that were never built**, found 8
    September 2026 by laying the running list beside `history-list-1440.png`.
    Four runs make them look idle. The owner runs Louisiana and East Texas every
    week, so this list gains a hundred rows a year and never loses one: it is
    the only place a movement run can be looked at once its photographs are
    gone.
  */
  const [territory, setTerritory] = useState('all');
  const [when, setWhen] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    const { runs: rows, error } = await listRuns();
    if (error) toast.error('Could not read the run history: ' + error.message);
    setRuns(rows);
    setLoading(false);
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    if (!openId) { setOpen(null); return undefined; }
    (async () => {
      const { run, lines, error } = await getRun(openId);
      if (!alive) return;
      if (error) { toast.error('Could not open that run: ' + error.message); setOpenId(null); return; }
      setOpen({ run, lines });
    })();
    return () => { alive = false; };
  }, [openId, toast]);

  const territories = useMemo(
    () => [...new Set(runs.map(r => r.territory).filter(Boolean))].sort(),
    [runs],
  );

  const WITHIN = { 30: 'The last 30 days', 90: 'The last 3 months', 365: 'The last year' };
  const shown = useMemo(() => {
    const q = search.trim().toUpperCase();
    return runs.filter(r => {
      if (territory !== 'all' && r.territory !== territory) return false;
      if (when !== 'all') {
        const days = (Date.now() - new Date(r.ran_at).getTime()) / 86400000;
        if (days > Number(when)) return false;
      }
      if (!q) return true;
      return (r.name || '').toUpperCase().includes(q)
        || (r.ran_by_name || '').toUpperCase().includes(q);
    });
    // WITHIN is a constant table; the three values that change are here.
  }, [runs, search, territory, when]);

  /*
    **Making the file again, months later, out of the lines rather than the
    pages.** It is the whole argument for keeping them: the photographs are
    gone, so this is the only way the file that went to the receiving system can
    ever be produced a second time.

    A line that was left out stays left out. It was held back for a reason that
    is still true, and quietly including it now would make the second file
    different from the first without saying so.
  */
  function makeFileAgain() {
    const rows = (open?.lines || [])
      .filter(l => l.outcome !== 'left_out')
      .map(l => ({
        destination: l.destination || '',
        itemNumber: l.item_number || '',
        quantity: l.quantity ?? '',
        unit: l.unit || '',
        date: '',
      }));
    if (!rows.length) { toast.error('This run has no lines that went into a file.'); return; }
    /*
      **Two downloadCsv functions exist in OnHand and they take different
      things.** The one in `movementUtils` takes a finished CSV string; the one
      in `reportData` takes rows and builds the string itself. Importing the
      wrong one here would have thrown on `.map` the first time anybody pressed
      this, months after the run it is about.
    */
    const { csv, written } = buildV2Csv(rows, open.run.ships_from || '', open.run.ran_by_name || '');
    if (!written) { toast.error('None of this run\u2019s lines could go into a file.'); return; }
    const name = (open.run.file_name || ('OnHand_Movement_Import_' + dayShort(open.run.ran_at)))
      .replace(/\.csv$/i, '');
    downloadCsv(csv, name + '.csv');
    toast.success('Made ' + name + '.csv again from the ' + written + ' lines this run kept.');
  }

  /*
    **A copy for your records**, which the approved drawing puts on the bar.
    Every run this company has ever done, as one file. It is the whole history
    rather than one run, because one run already has its own file and this is
    the thing you would hand to somebody asking what moved this quarter.
  */
  function exportHistory() {
    const header = ['Run', 'Territory', 'Ran by', 'When it ran', 'Pages', 'Lines',
                    'Locations', 'Photographs', 'File it made'];
    const body = shown.map(r => [
      r.name, r.territory || '', r.ran_by_name || '', dayShortAndClock(r.ran_at),
      r.page_count, r.line_count, r.location_count || 0,
      r.photos_deleted_at ? 'Deleted ' + dayShort(r.photos_deleted_at) : 'Still held',
      r.file_name || '',
    ]);
    downloadRows([header, ...body], 'OnHand_Movement_Run_History');
    toast.success(shown.length + ' run' + (shown.length === 1 ? '' : 's') + ' exported.');
  }

  // The page bar's own row belongs to whichever state is showing. It is handed
  // up rather than drawn here, so the bar stays one component (rule 26).
  useEffect(() => {
    if (!setBar) return;
    if (open?.run) {
      setBar({
        /* Which run this is, and who read it. It said neither: the left of the
           bar was empty and the only clue was the file name halfway down. */
        title: open.run.name,
        subtitle: open.run.ran_by_name
          ? 'Read by ' + open.run.ran_by_name + ' on ' + dayShortAndClock(open.run.ran_at)
          : 'Read on ' + dayShortAndClock(open.run.ran_at),
        meta: open.lines.length + (open.lines.length === 1 ? ' line' : ' lines'),
        quiet: (
          <button className="tb-btn tb-btn-ghost" onClick={() => setOpenId(null)}>Back to the history</button>
        ),
        actions: (
          <button className="tb-btn tb-btn-primary" onClick={makeFileAgain}>Make the file again</button>
        ),
      });
    } else {
      setBar({
        meta: runs.length ? shown.length + (shown.length === 1 ? ' run' : ' runs') : 'No runs yet',
        search: runs.length ? { value: search, onChange: setSearch, placeholder: 'Search a run' } : null,
        filterCount: (territory !== 'all' ? 1 : 0) + (when !== 'all' ? 1 : 0),
        drawer: runs.length ? (
          <>
            {territories.length > 1 && (
              <>
                <span className="tb-lbl">Territory</span>
                <select className={'tb-select' + (territory !== 'all' ? ' set' : '')}
                  value={territory} onChange={e => setTerritory(e.target.value)}>
                  <option value="all">All territories</option>
                  {territories.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </>
            )}
            <span className="tb-lbl">When</span>
            <select className={'tb-select' + (when !== 'all' ? ' set' : '')}
              value={when} onChange={e => setWhen(e.target.value)}>
              <option value="all">Any date</option>
              {Object.entries(WITHIN).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <span style={{ flex: 1 }} />
            {(territory !== 'all' || when !== 'all') && (
              <button className="tb-btn tb-btn-danger"
                onClick={() => { setTerritory('all'); setWhen('all'); }}>
                Clear filters
              </button>
            )}
          </>
        ) : null,
        quiet: runs.length
          ? <button className="tb-btn tb-btn-ghost" onClick={exportHistory}>A copy for your records</button>
          : null,
        // The list of runs has no primary act on purpose: you did not come to a
        // history to do something. Rule 33.
        actions: null,
      });
    }
    // makeFileAgain and exportHistory both read `open` and `shown`, which are
    // already covered by the two dependencies that produce them.
  }, [open, runs.length, shown.length, search, territory, when, territories.length, setBar]); // eslint-disable-line

  if (loading) {
    return <PageLoading rows={6} />;
  }

  /* -- Nothing has ever been run ---------------------------------------- */
  if (!runs.length) {
    return (
      <EmptyScreen
        title="No movement runs yet"
        says={'Every time pages are read, OnHand keeps what it found: the date it ran, who ran it, '
            + 'every line and where each one went. The photographs are deleted once the review is '
            + 'done and the lines stay here, so a run can be looked at again and its file made again.'}
        action={onGoToPages ? { label: 'Go to Pages', onClick: onGoToPages } : null}
      />
    );
  }

  /* -- One run, opened -------------------------------------------------- */
  if (open?.run) {
    const { run, lines } = open;
    const leftOut = lines.filter(l => l.outcome === 'left_out').length;
    return (
      <div>
        <Figures>
          <Figure n={run.page_count} label="Pages read" />
          <Figure n={lines.length} label="Lines" />
          <Figure n={locationsIn(lines)} label="Locations" />
          <Figure n={leftOut} label="Left out of the file" tone={leftOut ? 'var(--critical-ink)' : undefined} />
        </Figures>

        {/*
          **The owner's rule, said on the screen.** Whether the pictures are
          gone is the first thing this screen answers, because it is the thing
          he asked for and the thing that would otherwise be invisible.
        */}
        <div className={'mv-note' + (run.photos_deleted_at ? '' : ' held')}>
          <div className="mv-note-title">
            {run.photos_deleted_at
              ? 'The photographs were deleted on ' + dayShortAndClock(run.photos_deleted_at)
              : 'This run is still holding its photographs'}
          </div>
          <div className="mv-note-says">
            {run.photos_deleted_at
              ? 'They are removed once the review is finished, so nothing handwritten is kept. The '
                + lines.length + ' lines below are the record of this run, and the file can be made again from them.'
              : "They are removed once the review is finished. Until then they are held in this company's own private storage."}
          </div>
        </div>

        {run.file_name && (
          <div className="card mv-file">
            <span className="mv-file-label">The file this run made</span>
            <span className="mv-file-name">{run.file_name}</span>
          </div>
        )}

        <div className="rplist">
          <ListGrid
            columns={[
              { label: 'Written', width: 'minmax(0,0.9fr)' },
              { label: 'Went to', width: 'minmax(0,1.4fr)' },
              { label: 'Item number', width: 'minmax(0,0.9fr)' },
              { label: 'Description', width: 'minmax(0,1.6fr)' },
              { label: 'Qty', width: '90px', align: 'right' },
              { label: 'Result', width: '150px' },
            ]}
            rows={lines}
            keyOf={l => l.id}
            rowClass={l => 'mv-line ' + (OUTCOME[l.outcome] || OUTCOME.in_file).cls}
            cells={l => [
              <span className="mv-written">{'"' + (l.written || '') + '"'}</span>,
              l.destination
                ? <span>{l.destination}</span>
                : <span style={{ color: 'var(--text-secondary)' }}>No location</span>,
              <span className="mv-item">{l.item_number}</span>,
              l.description
                ? <span>{l.description}</span>
                : <span style={{ color: 'var(--text-secondary)' }}>Not in the catalog</span>,
              <span>{l.quantity}{l.unit ? <span className="mv-unit"> {l.unit}</span> : null}</span>,
              <Outcome value={l.outcome} />,
            ]}
            stack={l => (
              <span>
                <span className="mv-item" style={{ display: 'block' }}>{l.item_number}</span>
                <span style={{ display: 'block', fontSize: 13, marginTop: 2 }}>
                  {l.description || <span style={{ color: 'var(--text-secondary)' }}>Not in the catalog</span>}
                </span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>
                  {l.destination || <span style={{ color: 'var(--text-secondary)' }}>No location</span>}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    Written {'"' + (l.written || '') + '"'}
                  </span>
                  <Outcome value={l.outcome} />
                </span>
              </span>
            )}
          />
        </div>
      </div>
    );
  }

  /* -- The list of runs ------------------------------------------------- */
  return (
    <div className="rplist">
      {shown.length === 0 ? (
        /* It named the search term alone, and a filter can empty this list
           without anything being typed. */
        <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
          {search.trim()
            ? <>No run matches "{search.trim()}".</>
            : 'No run matches what you are looking for.'}
        </div>
      ) : (
        <ListGrid
          columns={[
            { label: 'Run', width: 'minmax(0,1.2fr)' },
            { label: 'Ran by', width: 'minmax(0,1.2fr)' },
            { label: 'When it ran', width: 'minmax(0,1fr)' },
            { label: 'Pages', width: '90px', align: 'right' },
            { label: 'Lines', width: '90px', align: 'right' },
            { label: 'Photographs', width: '220px' },
          ]}
          rows={shown}
          keyOf={r => r.id}
          onRowClick={r => setOpenId(r.id)}
          /* No coloured edge on this list. The Photographs chip already says
             which state a run is in, in words and in colour, and the approved
             drawing has one mark for it rather than two. */
          cells={r => [
            <span className="mv-run-name">{r.name}</span>,
            <span>{r.ran_by_name || <span style={{ color: 'var(--text-secondary)' }}>Nobody recorded</span>}</span>,
            <span>{dayShortAndClock(r.ran_at)}</span>,
            <span>{r.page_count}</span>,
            <span>{r.line_count}</span>,
            <Photos deletedAt={r.photos_deleted_at} />,
          ]}
          stack={r => (
            <span>
              <span className="mv-run-name" style={{ display: 'block' }}>{r.name}</span>
              <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3 }}>
                {dayShortAndClock(r.ran_at)}{r.ran_by_name ? ' - ' + r.ran_by_name : ''}
              </span>
              <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>
                {r.page_count} pages - {r.line_count} lines
                {r.location_count ? ' \u00b7 ' + r.location_count + ' locations' : ''}
              </span>
              <span style={{ display: 'block', marginTop: 8 }}><Photos deletedAt={r.photos_deleted_at} /></span>
            </span>
          )}
        />
      )}
    </div>
  );
}
